/**
 * Generates stamp placement points using grid-based distribution with jitter
 * Points are deterministic based on world position and seed
 */
import { CHUNK_SIZE, CHUNK_WORLD_SIZE, VOXEL_SCALE } from '../../voxel/constants.js';
import { StampType, getStamp, getStampFootprintRadius, isBuildingStamp } from './registry.js';
import { hashInt2, makeRng } from '../../util/rng.js';
import { MAX_STAMP_REACH_M } from './types/treeOakLarge.js';
import { packPairKey } from '../../util/packedKey.js';
import { flippedBounds, stampFlip } from './StampPlacer.js';
// ============== Default Configuration ==============
export const DEFAULT_STAMP_DISTRIBUTION = {
    seed: 54321,
    distributions: [
        // Buildings generated first (priority -10 to -5) - they claim the most space
        {
            type: StampType.BUILDING_SMALL,
            priority: -10,
            gridSize: 50, // Very sparse - one per 80m grid cell
            jitter: 0.35, // Some jitter but not too much
            exclusionRadius: 20, // 20m exclusion - large clearance around buildings
            coverage: 1, // landmarks are never coverage-thinned: their spacing is the point
        },
        {
            type: StampType.BUILDING_HUT,
            priority: -5,
            gridSize: 30, // Slightly denser than small buildings
            jitter: 0.4,
            exclusionRadius: 15, // 15m exclusion - huts need clearance too
            coverage: 1,
        },
        {
            type: StampType.BUILDING_TOWER,
            priority: -12, // Generated first - tall landmark claims the most space
            gridSize: 70, // Very sparse - rare landmark
            jitter: 0.35,
            exclusionRadius: 25, // 25m exclusion - big clearance around towers
            coverage: 1,
        },
        // Rocks generated after buildings (priority 0-2) - they claim space
        {
            type: StampType.ROCK_LARGE,
            priority: 0,
            gridSize: 17, // 24 m at full coverage → 17 m at half: the same count, in clumps
            jitter: 0.4, // ±40% jitter from cell center
            exclusionRadius: 1, // 1m exclusion from other stamps
            coverage: 0.5,
        },
        {
            type: StampType.ROCK_MEDIUM,
            priority: 1,
            gridSize: 7, // 10 m → 7 m at half coverage
            jitter: 0.4,
            exclusionRadius: 1,
            coverage: 0.5,
        },
        {
            type: StampType.ROCK_SMALL,
            priority: 2,
            gridSize: 4.2, // 6 m → 4.2 m at half coverage
            jitter: 0.35,
            exclusionRadius: 1, // No exclusion - can be close to others
            coverage: 0.5,
        },
        /**
         * The big oak: after the buildings, BEFORE the rocks, and long before the small trees.
         *
         * `isValidPlacement` compares a candidate against everything already placed in the chunk using the
         * LARGER of the two exclusion radii, so a priority decides which side of that comparison this
         * distribution is on. Placed early, the oak claims 8 m and the rocks and trees keep out of its
         * canopy — which is the point. Placed LATE (priority 5, between the rocks and the trees, which is
         * where a "sparse landmark under the trees" reads like it belongs) the 8 m is turned against it:
         * small rocks are one per 6 m grid, a chunk is 8 m across, so nearly every candidate lands within
         * 8 m of a rock already in the chunk and is rejected. Measured over 512 x 512 m: 10 oaks placed of
         * 334 grid cells at priority 5, against 293 at -3. A tree that exists 3% of the time is not a
         * distribution, so it goes ahead of the rocks and costs them ~8% of their own count.
         */
        {
            type: StampType.TREE_OAK_LARGE,
            priority: -3, // Ahead of the rocks — see above
            gridSize: 40, // Sparse - a landmark, not a forest; 30 m crowns must not interpenetrate
            jitter: 0.4,
            exclusionRadius: 8, // 8m exclusion - the small trees keep out of its canopy
            coverage: 1,
        },
        // Trees generated after rocks (priority 10) - single unified grid, random type selection
        {
            type: StampType.TREE_OAK, // Primary type (used for hashing, actual type picked from pool)
            priority: 10,
            gridSize: 3.5, // 5 m → 3.5 m at half coverage: one tree per 25 m² either way
            jitter: 0.6,
            exclusionRadius: 1,
            coverage: 0.5,
            typePool: [StampType.TREE_OAK, StampType.TREE_PINE], // Randomly pick between oak and pine
        },
    ],
};
// ============== Point Generator ==============
/**
 * HOW FAR THE NEIGHBOUR WALK GOES. A stamp reaches into a chunk from as far as its own half-extent,
 * so the walk must cover every fine chunk within the widest stamp's reach — the big oak's, declared
 * by the one module that knows it. Derived here rather than typed so the two cannot drift: a crown
 * allowed past the walk is missing from chunks it reaches, sliced off at a chunk plane.
 */
const REACH_CHUNKS = Math.ceil(MAX_STAMP_REACH_M / VOXEL_SCALE / CHUNK_SIZE);
/** Fine chunks either side of a chunk to scan: the margin's own reach, or the widest stamp's. */
const neighbourWalk = (margin) => Math.max(Math.ceil(margin / CHUNK_SIZE) + 1, REACH_CHUNKS);
const POINT_CACHE_MAX = 4096;
export class StampPointGenerator {
    config;
    /**
     * Raw points per fine chunk, memoised. Every chunk's walk asks for (2·REACH_CHUNKS + 1)² neighbours
     * and neighbouring chunks ask for mostly the same ones; unmemoised, the walk to 3 chunks was
     * ~2× the scatter cost of the walk to 2. Bounded; cleared wholesale when full.
     */
    pointCache = new Map();
    constructor(config = {}) {
        this.config = { ...DEFAULT_STAMP_DISTRIBUTION, ...config };
        if (config.distributions) {
            this.config.distributions = config.distributions;
        }
    }
    /**
     * Get seed for configuration
     */
    getSeed() {
        return this.config.seed;
    }
    /**
     * Generate stamp placements for a chunk and its neighbors that may overlap
     * @param cx - Chunk X coordinate
     * @param cz - Chunk Z coordinate
     * @param margin - Extra margin in voxels to check for overlapping stamps
     * @returns Array of stamp placements
     */
    generateForChunk(cx, cz, margin = 16) {
        const placements = [];
        // Check neighboring chunks that might have stamps overlapping into this chunk
        const chunkMargin = neighbourWalk(margin);
        for (let dz = -chunkMargin; dz <= chunkMargin; dz++) {
            for (let dx = -chunkMargin; dx <= chunkMargin; dx++) {
                const neighborCx = cx + dx;
                const neighborCz = cz + dz;
                // Generate points for this neighbor chunk
                const neighborPlacements = this.generateChunkPoints(neighborCx, neighborCz);
                // Filter to only include placements that affect the target chunk
                for (const placement of neighborPlacements) {
                    if (this.stampAffectsChunk(placement, cx, cz, margin)) {
                        placements.push(placement);
                    }
                }
            }
        }
        return placements;
    }
    /**
     * Placements affecting a COARSE (lod >= 1) chunk's footprint.
     *
     * A coarse chunk spans 2^lod fine chunks per axis, and the scatter's spacing rules are CHUNK-LOCAL
     * (isValidPlacement compares against the placements accumulated for that fine chunk, and buildings
     * claim space before trees). So the footprint is walked as fine chunks and fed through the SAME
     * generateChunkPoints as LOD 0 — a coarsened scatter would give trees different positions at
     * different levels, i.e. trees visibly jumping as you walk toward them.
     *
     * Every distribution is still generated even though only migrated types can be placed: the
     * exclusion pass is order-dependent across types, so skipping buildings would move the trees.
     * The filter is therefore applied at the END, via `keep`.
     */
    generateForLodChunk(cx, cz, lod, keep, footprintRadius, margin = 16) {
        const stride = 1 << lod;
        const fx0 = cx * stride, fz0 = cz * stride;
        const chunkMargin = neighbourWalk(margin);
        // Coarse chunk footprint in FINE voxels, grown by the margin.
        const minVX = fx0 * CHUNK_SIZE - margin;
        const maxVX = (fx0 + stride) * CHUNK_SIZE + margin;
        const minVZ = fz0 * CHUNK_SIZE - margin;
        const maxVZ = (fz0 + stride) * CHUNK_SIZE + margin;
        const placements = [];
        for (let fz = fz0 - chunkMargin; fz < fz0 + stride + chunkMargin; fz++) {
            for (let fx = fx0 - chunkMargin; fx < fx0 + stride + chunkMargin; fx++) {
                for (const placement of this.generateChunkPoints(fx, fz)) {
                    if (!keep(placement.type))
                        continue;
                    const r = footprintRadius(placement);
                    const vx = placement.worldX / VOXEL_SCALE;
                    const vz = placement.worldZ / VOXEL_SCALE;
                    if (vx + r >= minVX && vx - r <= maxVX && vz + r >= minVZ && vz - r <= maxVZ) {
                        placements.push(placement);
                    }
                }
            }
        }
        return placements;
    }
    /**
     * Generate raw stamp points within a single chunk (no neighbor check)
     * Uses fixed grid per stamp type with jitter, processes in priority order
     */
    generateChunkPoints(cx, cz) {
        const key = packPairKey(cx, cz);
        const held = this.pointCache.get(key);
        if (held)
            return held;
        if (this.pointCache.size >= POINT_CACHE_MAX)
            this.pointCache.clear();
        const placements = this.generateChunkPointsUncached(cx, cz);
        this.pointCache.set(key, placements);
        return placements;
    }
    generateChunkPointsUncached(cx, cz) {
        const placements = [];
        const chunkWorldX = cx * CHUNK_WORLD_SIZE;
        const chunkWorldZ = cz * CHUNK_WORLD_SIZE;
        // Sort distributions by priority (lower = first)
        const sortedDists = [...this.config.distributions].sort((a, b) => a.priority - b.priority);
        for (const dist of sortedDists) {
            // Generate grid cells that overlap this chunk
            const gridCells = this.getGridCellsForChunk(cx, cz, dist.gridSize);
            for (const cell of gridCells) {
                // Deterministic random for this cell
                const rand = makeRng(this.hashGridCell(cell.gx, cell.gz, dist.type));
                // COVERAGE: the cell may spawn nothing — see StampDistribution.coverage. Rolled first so the
                // jitter stream below is the same whether or not a distribution thins.
                if (rand() >= (dist.coverage ?? 1))
                    continue;
                // Calculate cell centroid in world space
                const cellCenterX = (cell.gx + 0.5) * dist.gridSize;
                const cellCenterZ = (cell.gz + 0.5) * dist.gridSize;
                // Apply jitter (±jitter% from center)
                const maxJitter = dist.gridSize * dist.jitter;
                const jitterX = (rand() * 2 - 1) * maxJitter;
                const jitterZ = (rand() * 2 - 1) * maxJitter;
                const worldX = cellCenterX + jitterX;
                const worldZ = cellCenterZ + jitterZ;
                // Skip if outside this chunk (will be handled by that chunk)
                if (worldX < chunkWorldX || worldX >= chunkWorldX + CHUNK_WORLD_SIZE ||
                    worldZ < chunkWorldZ || worldZ >= chunkWorldZ + CHUNK_WORLD_SIZE) {
                    continue;
                }
                // Check collision with existing placements
                if (!this.isValidPlacement(worldX, worldZ, dist, placements)) {
                    continue;
                }
                // Select stamp type: from pool (random) or single type
                let stampType = dist.type;
                if (dist.typePool && dist.typePool.length > 0) {
                    const typeIndex = Math.floor(rand() * dist.typePool.length);
                    stampType = dist.typePool[typeIndex];
                }
                // Select variant (0-3)
                const variant = Math.floor(rand() * 4);
                placements.push({
                    type: stampType,
                    variant,
                    worldX,
                    worldZ,
                    rotation: rand() * Math.PI * 2,
                });
            }
        }
        return placements;
    }
    /**
     * Get all grid cells that could place a stamp in or near this chunk
     */
    getGridCellsForChunk(cx, cz, gridSize) {
        const chunkWorldX = cx * CHUNK_WORLD_SIZE;
        const chunkWorldZ = cz * CHUNK_WORLD_SIZE;
        // Find grid cell range that could affect this chunk (with 1 cell margin for jitter)
        const minGx = Math.floor((chunkWorldX - gridSize) / gridSize);
        const maxGx = Math.floor((chunkWorldX + CHUNK_WORLD_SIZE + gridSize) / gridSize);
        const minGz = Math.floor((chunkWorldZ - gridSize) / gridSize);
        const maxGz = Math.floor((chunkWorldZ + CHUNK_WORLD_SIZE + gridSize) / gridSize);
        const cells = [];
        for (let gz = minGz; gz <= maxGz; gz++) {
            for (let gx = minGx; gx <= maxGx; gx++) {
                cells.push({ gx, gz });
            }
        }
        return cells;
    }
    /**
     * Check if a placement is valid (not too close to existing stamps)
     * Grid spacing handles same-type collision, this checks cross-type exclusion
     */
    isValidPlacement(worldX, worldZ, dist, existingPlacements) {
        const exclusionRadius = dist.exclusionRadius;
        for (const existing of existingPlacements) {
            const dx = worldX - existing.worldX;
            const dz = worldZ - existing.worldZ;
            const distSq = dx * dx + dz * dz;
            // Check cross-type exclusion
            // Use the larger of the two exclusion radii
            const existingDist = this.config.distributions.find(d => d.type === existing.type);
            const existingExclusion = existingDist?.exclusionRadius ?? 0;
            const combinedExclusion = Math.max(exclusionRadius, existingExclusion);
            // Skip if neither has exclusion
            if (combinedExclusion <= 0)
                continue;
            if (distSq < combinedExclusion * combinedExclusion) {
                return false;
            }
        }
        return true;
    }
    /**
     * Check if a stamp placement affects a chunk (considering stamp bounds)
     */
    stampAffectsChunk(placement, cx, cz, margin) {
        // Convert placement to voxel coordinates
        const stampVoxelX = placement.worldX / VOXEL_SCALE;
        const stampVoxelZ = placement.worldZ / VOXEL_SCALE;
        // Stamp X/Z footprint in world voxels. Buildings use a cheap rotation-invariant footprint radius
        // (no ~95k-sample SDF generation per candidate — the big exploration cost); trees/rocks read their
        // exact bounds, which are cached per type:variant (rotation-independent → a free lookup).
        let stampMinX, stampMaxX, stampMinZ, stampMaxZ;
        if (isBuildingStamp(placement.type)) {
            const r = getStampFootprintRadius(placement.type, placement.variant);
            stampMinX = stampVoxelX - r;
            stampMaxX = stampVoxelX + r;
            stampMinZ = stampVoxelZ - r;
            stampMaxZ = stampVoxelZ + r;
        }
        else {
            const stamp = getStamp(placement.type, placement.variant, placement.rotation);
            if (!stamp)
                return false; // no LOD-0 model for this type
            // As the instance is MIRRORED (stampFlip): the placer's own arithmetic, so the two agree.
            const bounds = flippedBounds(stamp.bounds, stampFlip(placement.worldX, placement.worldZ));
            stampMinX = stampVoxelX + bounds.minX;
            stampMaxX = stampVoxelX + bounds.maxX;
            stampMinZ = stampVoxelZ + bounds.minZ;
            stampMaxZ = stampVoxelZ + bounds.maxZ;
        }
        // Chunk bounds in voxels
        const chunkMinVoxelX = cx * CHUNK_SIZE;
        const chunkMinVoxelZ = cz * CHUNK_SIZE;
        const chunkMaxVoxelX = chunkMinVoxelX + CHUNK_SIZE;
        const chunkMaxVoxelZ = chunkMinVoxelZ + CHUNK_SIZE;
        // Check overlap with margin
        return stampMaxX >= chunkMinVoxelX - margin &&
            stampMinX <= chunkMaxVoxelX + margin &&
            stampMaxZ >= chunkMinVoxelZ - margin &&
            stampMinZ <= chunkMaxVoxelZ + margin;
    }
    /**
     * Hash for global grid cell (not chunk-relative)
     */
    /**
     * A cell's seed. A REAL hash, because this is where the scatter's randomness comes from and it was
     * not random: `seed·31 + gx`, `·31 + gz` is affine in the cell index, and the first outputs of the
     * LCG that follows are affine in the seed, so the jitter of neighbouring cells differed by a
     * CONSTANT — measured −0.07 of the range per cell one way and −0.18 the other, wrapping every 6–14
     * cells. The "random" offsets sheared the lattice instead of breaking it, and every tree stood in a
     * slightly skewed row. `hashInt2` avalanches, so neighbouring cells are unrelated. The type is hashed
     * whole: its first character was 't' for every tree type, so they all shared one stream.
     */
    hashGridCell(gx, gz, type) {
        let t = this.typeHash.get(type);
        if (t === undefined) {
            t = 2166136261 >>> 0;
            for (let i = 0; i < type.length; i++)
                t = Math.imul(t ^ type.charCodeAt(i), 16777619) >>> 0;
            this.typeHash.set(type, t);
        }
        return hashInt2(hashInt2(this.config.seed, t), hashInt2(gx, gz));
    }
    typeHash = new Map();
}
//# sourceMappingURL=StampPointGenerator.js.map